import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api";
import { agentKeys } from "../queryKeys";

interface ConversationSummary {
  id: string;
  visitor_id: string;
  created_at: string;
  message_count: number;
  last_message: string;
}

interface Message {
  id: string;
  role: string;
  content: string;
  created_at: string;
}

interface ConversationDetail {
  id: string;
  visitor_id: string;
  created_at: string;
  messages: Message[];
}

export function ChatsPage({ agentId }: { agentId: string }) {
  const queryClient = useQueryClient();
  const [selectedChat, setSelectedChat] = useState<string | null>(null);
  const [error, setError] = useState("");

  const { data: conversations = [] } = useQuery({
    queryKey: agentKeys.conversations(agentId),
    queryFn: () =>
      apiFetch<{ conversations: ConversationSummary[] }>(
        `/api/agents/${agentId}/conversations`
      ).then((r) => r.conversations),
  });

  const { data: chatDetail, isLoading: loadingDetail } = useQuery({
    queryKey: agentKeys.conversation(agentId, selectedChat!),
    queryFn: () =>
      apiFetch<{ conversation: ConversationDetail }>(
        `/api/agents/${agentId}/conversations/${selectedChat}`
      ).then((r) => r.conversation),
    enabled: !!selectedChat,
  });

  const deleteMutation = useMutation({
    mutationFn: (convId: string) =>
      apiFetch(`/api/agents/${agentId}/conversations/${convId}`, { method: "DELETE" }),
    onSuccess: (_, convId) => {
      queryClient.invalidateQueries({ queryKey: agentKeys.conversations(agentId) });
      queryClient.invalidateQueries({
        queryKey: agentKeys.conversation(agentId, convId),
      });
    },
    onError: (err: Error) => setError(err.message || "Delete failed"),
  });

  async function handleDelete(convId: string, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm("Delete this conversation? This cannot be undone.")) return;
    setError("");
    try {
      await deleteMutation.mutateAsync(convId);
      if (selectedChat === convId) setSelectedChat(null);
    } catch {
      // error set in onError
    }
  }

  const deletingId =
    deleteMutation.isPending && deleteMutation.variables != null
      ? deleteMutation.variables
      : null;
  const loading = loadingDetail;

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString();
  }

  function formatTime(iso: string) {
    return new Date(iso).toLocaleTimeString();
  }

  return (
    <div className="chats-page">
      <div className="chats-list-panel">
        <h2>Chats</h2>
        {error && <div className="auth-error">{error}</div>}
        
        {conversations.length === 0 ? (
          <p className="dash-empty">No conversations yet.</p>
        ) : (
          <div className="chats-list">
            {conversations.map(c => (
              <div
                key={c.id}
                className={`chat-item ${selectedChat === c.id ? "chat-item-selected" : ""}`}
                onClick={() => setSelectedChat(c.id)}
              >
                <div className="chat-item-header">
                  <span className="chat-visitor-id">
                    {c.visitor_id.slice(0, 12)}...
                  </span>
                  <span className="chat-date">{formatDate(c.created_at)}</span>
                </div>
                <div className="chat-preview-text">{c.last_message || "No messages"}</div>
                <div className="chat-item-footer">
                  <span className="chat-message-count">{c.message_count} messages</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger chat-delete-btn"
                    onClick={e => handleDelete(c.id, e)}
                    disabled={deletingId === c.id}
                  >
                    {deletingId === c.id ? "..." : "Delete"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="chats-content-panel">
        {selectedChat ? (
          loading ? (
            <div className="chats-content-empty">
              <p>Loading conversation...</p>
            </div>
          ) : chatDetail ? (
            <div className="chats-content">
              <div className="chats-content-header">
                <h3>Conversation Details</h3>
                <div className="chat-meta-info">
                  <span>Visitor: {chatDetail.visitor_id}</span>
                  <span>Started: {formatDate(chatDetail.created_at)}</span>
                  <span>{chatDetail.messages.length} messages</span>
                </div>
              </div>
              <div className="chats-messages">
                {chatDetail.messages.map(m => (
                  <div key={m.id} className={`chat-message-item chat-message-${m.role}`}>
                    <div className="chat-message-header">
                      <span className="chat-message-role">{m.role}</span>
                      <span className="chat-message-time">{formatTime(m.created_at)}</span>
                    </div>
                    <div className="chat-message-content">{m.content}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="chats-content-empty">
              <p>Could not load conversation.</p>
            </div>
          )
        ) : (
          <div className="chats-content-empty">
            <p>Select a conversation to view messages</p>
          </div>
        )}
      </div>
    </div>
  );
}
